import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box
} from '@mui/material';
import { Close } from '@mui/icons-material';
import FolderPermissionsTable from './FolderPermissionsTable';
import useFolderPermissions from './useFolderPermissions';
import { bool, func, string, number } from 'prop-types';

/**
 * @component FolderPermissionsModal
 * 
 * Material UI Dialog for CANshare folder permissions management.
 * - Header with static "CANshare Folder Permissions" title
 * - Button-based bulk operations in toolbar
 * - 2-column table layout (Folder Name | Permission)
 * - Permission counter in footer
 * - Red cancel button, blue save button
 * - Supports both create mode (user creation) and edit mode (managing existing user)
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {function} props.onClose - Callback when modal should close
 * @param {string} props.mode - "create" or "edit" mode
 * @param {number|null} props.userId - User ID for edit mode (required), null for create
 * @param {function} props.onPermissionsSet - Callback for create mode with draft permissions
 * @param {function} props.onAutoSave - Callback for edit mode after save
 * @returns {JSX.Element}
 */
function FolderPermissionsModal({ 
    open, 
    onClose, 
    mode,           
    userId,         
    onPermissionsSet,  
    onAutoSave
}) {
    const {
        folders,
        loading,
        searchTerm,
        setSearchTerm,
        canUndo,
        handlePermissionChange,
        handleAllAdmin,
        handleAllView,
        handleClearAll,
        handleUndo,
        savePermissions
    } = useFolderPermissions(mode, userId);

    // Permission counter (excludes "None")
    const assignedCount = folders.filter(f => f.Permission !== "None").length;

    const handleClose = async () => {
        try {
            if (mode === "create") {
                // Draft mode: Only pass permissions to parent, don't save to database
                const selectedPermissions = folders.filter(f => f.Permission !== "None");
                if (onPermissionsSet) {
                    onPermissionsSet(selectedPermissions);
                }
                onClose();
            } else if (mode === "edit") {
                // Edit mode: Auto-save permissions immediately
                await savePermissions();
                if (onAutoSave) {
                    onAutoSave();
                }
                onClose();
            }
        } catch (error) {
            // Don't close modal if save failed in edit mode
            if (mode === "create") {
                onClose();
            }
        }
    };

    // Cancel without saving
    const handleCancel = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '80vh',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
            // Disable default close on backdrop click during save
            disableEscapeKeyDown={loading}
        >
            {/* Modal Title Header */}
            <DialogTitle sx={{ 
                m: 0, 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="div">
                        CANshare Folder Access Manager
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleCancel}
                    size="small"
                    disabled={loading}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Table Content with Toolbar */}
            <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
                <FolderPermissionsTable
                    folders={folders}
                    loading={loading}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onPermissionChange={handlePermissionChange}
                    onAllAdmin={handleAllAdmin}
                    onAllView={handleAllView}
                    onClearAll={handleClearAll}
                    onUndo={handleUndo}
                    canUndo={canUndo}
                    mode={mode}
                />
            </DialogContent>

            {/* Footer with Permission Counter and Action Buttons */}
            <DialogActions sx={{ 
                px: 3, 
                py: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="body2" color="text.secondary">
                    {assignedCount} permissions assigned.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                        onClick={handleCancel}
                        disabled={loading}
                        color="error"
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleClose} 
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        SAVE
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

FolderPermissionsModal.propTypes = {
    open: bool.isRequired,
    onClose: func.isRequired,
    mode: string.isRequired,
    userId: number,
    onPermissionsSet: func,
    onAutoSave: func
};

export default FolderPermissionsModal;
